/**
 * DealTagsManager — Super-Admin panel embedded inside Marketplace Uploads.
 *
 * Two panels side-by-side:
 *  1. Tag catalog: create / rename / delete / merge tags
 *  2. Deal assignments: list distinct deals from ssp_data with inline tag editor
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, GitMerge, Tag, Search, X, Check, UserPlus, UserMinus } from "lucide-react";
import { Card, CardContent } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import {
  listTags, createTag, renameTag, deleteTag, mergeTags,
  listTaggedDeals, tagChipStyle,
} from "./dealTagsApi";
import { listOwnerUsers, bulkSetDealOwners, fullUserLabel } from "./dealOwnersApi";
import DealTagChipEditor from "./DealTagChipEditor";
import DealOwnerEditor from "./DealOwnerEditor";

const DealTagsManager = ({ token, requestDelete }) => {
  const [tags, setTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(true);
  const [deals, setDeals] = useState([]);
  const [dealsLoading, setDealsLoading] = useState(true);
  const [dealQuery, setDealQuery] = useState("");
  const [onlyUntagged, setOnlyUntagged] = useState(false);

  const [newTagName, setNewTagName] = useState("");
  const [editing, setEditing] = useState(null);          // {tag_id, name}
  const [editingName, setEditingName] = useState("");
  const [merge, setMerge] = useState({ source: "", target: "" });
  const [users, setUsers] = useState([]);
  const [selectedDealIds, setSelectedDealIds] = useState([]);
  const [bulkUserId, setBulkUserId] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const tagsById = useMemo(() => {
    const m = new Map();
    tags.forEach((t) => m.set(t.tag_id, t));
    return m;
  }, [tags]);

  const refreshTags = useCallback(async () => {
    setTagsLoading(true);
    try {
      const data = await listTags(token);
      setTags(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load tags");
    } finally {
      setTagsLoading(false);
    }
  }, [token]);

  const refreshDeals = useCallback(async () => {
    setDealsLoading(true);
    try {
      const data = await listTaggedDeals(token, {
        q: dealQuery.trim() || undefined,
        only_untagged: onlyUntagged,
      });
      setDeals(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load deals");
    } finally {
      setDealsLoading(false);
    }
  }, [token, dealQuery, onlyUntagged]);

  useEffect(() => { refreshTags(); }, [refreshTags]);
  useEffect(() => { refreshDeals(); }, [refreshDeals]);

  useEffect(() => {
    listOwnerUsers(token).then(setUsers).catch(() => {});
  }, [token]);

  const visibleDeals = useMemo(() => deals.slice(0, 200), [deals]);

  const toggleDealSelect = (dealId) => setSelectedDealIds((prev) =>
    prev.includes(dealId) ? prev.filter((d) => d !== dealId) : [...prev, dealId]
  );

  const toggleSelectAllDeals = () => setSelectedDealIds((prev) =>
    prev.length === visibleDeals.length ? [] : visibleDeals.map((d) => d.deal_id)
  );

  const runBulkOwners = async (mode) => {
    if (selectedDealIds.length === 0 || !bulkUserId) return;
    setBulkBusy(true);
    try {
      const res = await bulkSetDealOwners(token, selectedDealIds, [bulkUserId], mode);
      const map = res.owner_map || {};
      setDeals((ds) => ds.map((d) => (
        selectedDealIds.includes(d.deal_id) ? { ...d, owner_user_ids: map[d.deal_id] || [] } : d
      )));
      toast.success(`${mode === "remove" ? "Revoked" : "Granted"} access on ${res.deal_count} deal(s)`);
      setSelectedDealIds([]);
      setBulkUserId("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk owner update failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const handleCreate = async () => {
    const name = newTagName.trim();
    if (!name) return;
    try {
      await createTag(token, name);
      setNewTagName("");
      toast.success(`Tag "${name}" created`);
      refreshTags();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create tag");
    }
  };

  const startEdit = (tag) => {
    setEditing(tag);
    setEditingName(tag.name);
  };

  const saveEdit = async () => {
    if (!editing) return;
    const name = editingName.trim();
    if (!name || name === editing.name) {
      setEditing(null);
      return;
    }
    try {
      await renameTag(token, editing.tag_id, name);
      toast.success("Tag renamed");
      setEditing(null);
      refreshTags();
      refreshDeals();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to rename");
    }
  };

  const handleDelete = (tag) => {
    if (!requestDelete) {
      if (!window.confirm(`Delete tag "${tag.name}"? It will be removed from ${tag.deal_count} deal(s).`)) return;
      deleteTag(token, tag.tag_id)
        .then(() => { toast.success("Tag deleted"); refreshTags(); refreshDeals(); })
        .catch((e) => toast.error(e?.response?.data?.detail || "Failed"));
      return;
    }
    requestDelete("deal_tag", tag.tag_id, `${tag.name} (used by ${tag.deal_count} deal${tag.deal_count === 1 ? "" : "s"})`, async () => {
      await deleteTag(token, tag.tag_id);
      toast.success("Tag deleted");
      refreshTags();
      refreshDeals();
    });
  };

  const handleMerge = async () => {
    if (!merge.source || !merge.target) return;
    if (merge.source === merge.target) {
      toast.error("Pick two different tags");
      return;
    }
    const src = tagsById.get(merge.source);
    const tgt = tagsById.get(merge.target);
    if (!window.confirm(`Merge "${src?.name}" into "${tgt?.name}"?\nAll deals tagged "${src?.name}" will be re-tagged with "${tgt?.name}", then "${src?.name}" will be deleted.`)) return;
    try {
      const res = await mergeTags(token, merge.source, merge.target);
      toast.success(`Merged — ${res.reassigned} deal(s) moved`);
      setMerge({ source: "", target: "" });
      refreshTags();
      refreshDeals();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    }
  };

  const dealTagChanged = (dealId, nextIds) => {
    setDeals((ds) => ds.map((d) => (d.deal_id === dealId ? { ...d, tag_ids: nextIds } : d)));
    refreshTags();  // usage counts may change
  };

  return (
    <div className="space-y-4" data-testid="deal-tags-manager">
      {/* ===== TAG CATALOG ===== */}
      <Card className="bg-[#1e2124] border-[#2a2d31]">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#a855f7]/15 flex items-center justify-center">
              <Tag className="w-4 h-4 text-[#a855f7]" />
            </div>
            <div className="flex-1">
              <p className="text-white text-xs font-mono font-medium">Deal Tags</p>
              <p className="text-[#ebebeb]/50 text-[11px] font-mono mt-0.5">
                Group deals across SSPs to slice performance in reporting
              </p>
            </div>
            <span className="text-[#ebebeb]/40 text-[11px] font-mono">
              {tags.length} tag{tags.length === 1 ? "" : "s"}
            </span>
          </div>

          {/* Create new */}
          <div className="flex items-center gap-2">
            <Input
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              placeholder="New tag name…"
              className="text-xs font-mono bg-[#1a1c1f] border-[#2a2d31] text-[#ebebeb] placeholder:text-[#ebebeb]/30 h-8"
              data-testid="new-tag-input"
              maxLength={40}
            />
            <button
              onClick={handleCreate}
              disabled={!newTagName.trim()}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-mono bg-[#a855f7]/15 text-[#a855f7] border border-[#a855f7]/30 hover:bg-[#a855f7]/25 disabled:opacity-40"
              data-testid="create-tag-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Create
            </button>
          </div>

          {/* Tag list */}
          {tagsLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-4 h-4 text-[#ff3737] animate-spin" />
            </div>
          ) : tags.length === 0 ? (
            <p className="text-[#ebebeb]/40 font-mono text-[11px] text-center py-4">
              No tags yet — create one above to start grouping deals.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {tags.map((t) => {
                const isEditing = editing?.tag_id === t.tag_id;
                return (
                  <div
                    key={t.tag_id}
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded border text-[11px] font-mono"
                    style={tagChipStyle(t.tag_id)}
                    data-testid={`tag-catalog-${t.tag_id}`}
                  >
                    {isEditing ? (
                      <>
                        <input
                          autoFocus
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && saveEdit()}
                          className="bg-transparent border-b border-current w-24 outline-none"
                          maxLength={40}
                          data-testid={`tag-rename-input-${t.tag_id}`}
                        />
                        <button onClick={saveEdit} className="opacity-70 hover:opacity-100" data-testid={`tag-rename-save-${t.tag_id}`}>
                          <Check className="w-3 h-3" />
                        </button>
                        <button onClick={() => setEditing(null)} className="opacity-70 hover:opacity-100">
                          <X className="w-3 h-3" />
                        </button>
                      </>
                    ) : (
                      <>
                        <span>{t.name}</span>
                        <span className="opacity-60">·{t.deal_count}</span>
                        <button
                          onClick={() => startEdit(t)}
                          className="opacity-50 hover:opacity-100"
                          aria-label="Rename"
                          data-testid={`tag-edit-${t.tag_id}`}
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => handleDelete(t)}
                          className="opacity-50 hover:opacity-100 hover:text-[#ff3737]"
                          aria-label="Delete"
                          data-testid={`tag-delete-${t.tag_id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Merge */}
          {tags.length >= 2 && (
            <div className="pt-3 border-t border-[#2a2d31]">
              <p className="text-[11px] font-mono text-[#ebebeb]/60 mb-2 flex items-center gap-1">
                <GitMerge className="w-3 h-3" /> Merge tags
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={merge.source}
                  onChange={(e) => setMerge((m) => ({ ...m, source: e.target.value }))}
                  className="text-xs font-mono bg-[#1a1c1f] border border-[#2a2d31] rounded px-2 py-1.5 text-[#ebebeb] focus:outline-none focus:border-[#ff3737]"
                  data-testid="merge-source"
                >
                  <option value="">Source tag…</option>
                  {tags.map((t) => <option key={t.tag_id} value={t.tag_id}>{t.name}</option>)}
                </select>
                <span className="text-[#ebebeb]/40 text-xs">→</span>
                <select
                  value={merge.target}
                  onChange={(e) => setMerge((m) => ({ ...m, target: e.target.value }))}
                  className="text-xs font-mono bg-[#1a1c1f] border border-[#2a2d31] rounded px-2 py-1.5 text-[#ebebeb] focus:outline-none focus:border-[#ff3737]"
                  data-testid="merge-target"
                >
                  <option value="">Target tag…</option>
                  {tags.filter((t) => t.tag_id !== merge.source).map((t) => (
                    <option key={t.tag_id} value={t.tag_id}>{t.name}</option>
                  ))}
                </select>
                <button
                  onClick={handleMerge}
                  disabled={!merge.source || !merge.target || merge.source === merge.target}
                  className="px-3 py-1.5 rounded text-xs font-mono bg-[#a855f7]/15 text-[#a855f7] border border-[#a855f7]/30 hover:bg-[#a855f7]/25 disabled:opacity-40"
                  data-testid="merge-btn"
                >
                  Merge
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ===== DEAL ASSIGNMENTS ===== */}
      <Card className="bg-[#1e2124] border-[#2a2d31]">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="w-8 h-8 rounded-lg bg-[#3b82f6]/15 flex items-center justify-center">
              <Search className="w-4 h-4 text-[#3b82f6]" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <p className="text-white text-xs font-mono font-medium">Deal Assignments</p>
              <p className="text-[#ebebeb]/50 text-[11px] font-mono mt-0.5">
                Apply tags and assign owners. Tags and owners follow the deal_id across all SSPs;
                owners only see their own deals in marketplace reporting.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                value={dealQuery}
                onChange={(e) => setDealQuery(e.target.value)}
                placeholder="Search deal id or name…"
                className="text-xs font-mono bg-[#1a1c1f] border-[#2a2d31] text-[#ebebeb] placeholder:text-[#ebebeb]/30 h-8 w-56"
                data-testid="deal-search-input"
              />
              <label className="flex items-center gap-1.5 text-[11px] font-mono text-[#ebebeb]/60 cursor-pointer">
                <input
                  type="checkbox"
                  checked={onlyUntagged}
                  onChange={(e) => setOnlyUntagged(e.target.checked)}
                  data-testid="only-untagged-toggle"
                />
                Only untagged
              </label>
            </div>
          </div>

          {selectedDealIds.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap p-2 rounded-lg bg-[#22c55e]/5 border border-[#22c55e]/25" data-testid="owner-bulk-bar">
              <span className="text-[11px] font-mono text-[#ebebeb]/70">
                <span className="text-[#22c55e]">{selectedDealIds.length}</span> deal{selectedDealIds.length === 1 ? "" : "s"} selected
              </span>
              <select
                value={bulkUserId}
                onChange={(e) => setBulkUserId(e.target.value)}
                className="text-xs font-mono bg-[#1a1c1f] border border-[#2a2d31] rounded px-2 py-1.5 text-[#ebebeb] focus:outline-none focus:border-[#22c55e] max-w-[320px]"
                data-testid="owner-bulk-select"
              >
                <option value="">Select user…</option>
                {users.map((u) => <option key={u.id} value={u.id}>{fullUserLabel(u)}</option>)}
              </select>
              <button
                onClick={() => runBulkOwners("add")}
                disabled={!bulkUserId || bulkBusy}
                className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-mono bg-[#22c55e]/15 text-[#22c55e] border border-[#22c55e]/30 hover:bg-[#22c55e]/25 disabled:opacity-40"
                data-testid="owner-bulk-grant"
              >
                {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />} Grant access
              </button>
              <button
                onClick={() => runBulkOwners("remove")}
                disabled={!bulkUserId || bulkBusy}
                className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-mono bg-[#ff3737]/15 text-[#ff3737] border border-[#ff3737]/30 hover:bg-[#ff3737]/25 disabled:opacity-40"
                data-testid="owner-bulk-revoke"
              >
                <UserMinus className="w-3.5 h-3.5" /> Revoke
              </button>
              <button onClick={() => setSelectedDealIds([])} className="text-[#ebebeb]/40 hover:text-white" data-testid="owner-bulk-clear">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {dealsLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-4 h-4 text-[#ff3737] animate-spin" />
            </div>
          ) : deals.length === 0 ? (
            <p className="text-[#ebebeb]/40 font-mono text-[11px] text-center py-6">
              No deals match — try a different search or upload SSP data first.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs font-mono">
                <thead>
                  <tr className="border-b border-[#2a2d31] text-[#ebebeb]/50">
                    <th className="w-8 py-2 px-2">
                      <input
                        type="checkbox"
                        checked={visibleDeals.length > 0 && selectedDealIds.length === visibleDeals.length}
                        onChange={toggleSelectAllDeals}
                        className="accent-[#22c55e] cursor-pointer"
                        data-testid="owner-select-all"
                      />
                    </th>
                    <th className="text-left py-2 px-2 font-normal">Deal Name / ID</th>
                    <th className="text-left py-2 px-2 font-normal w-40">SSP</th>
                    <th className="text-right py-2 px-2 font-normal w-24">Impressions</th>
                    <th className="text-right py-2 px-2 font-normal w-24">Spend</th>
                    <th className="text-left py-2 px-2 font-normal min-w-[200px]">Owner (reporting access)</th>
                    <th className="text-left py-2 px-2 font-normal min-w-[240px]">Tags</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleDeals.map((d) => (
                    <tr key={d.deal_id} className="border-b border-[#2a2d31]/50 hover:bg-white/[0.02]" data-testid={`deal-row-${d.deal_id}`}>
                      <td className="py-2 px-2">
                        <input
                          type="checkbox"
                          checked={selectedDealIds.includes(d.deal_id)}
                          onChange={() => toggleDealSelect(d.deal_id)}
                          className="accent-[#22c55e] cursor-pointer"
                          data-testid={`owner-select-${d.deal_id}`}
                        />
                      </td>
                      <td className="py-2 px-2">
                        <div className="text-[#ebebeb]/90 truncate max-w-[300px]" title={d.deal_name}>{d.deal_name || "(no name)"}</div>
                        <div className="text-[#ebebeb]/40 text-[10px] truncate max-w-[300px]" title={d.deal_id}>{d.deal_id}</div>
                      </td>
                      <td className="py-2 px-2 text-[#ebebeb]/70">
                        {(d.ssp_sources || []).join(", ")}
                      </td>
                      <td className="py-2 px-2 text-right text-[#ebebeb]/80">{d.impressions.toLocaleString()}</td>
                      <td className="py-2 px-2 text-right text-[#ebebeb]/80">${d.spend.toLocaleString()}</td>
                      <td className="py-2 px-2">
                        <DealOwnerEditor
                          token={token}
                          dealId={d.deal_id}
                          users={users}
                          value={d.owner_user_ids || []}
                          onChange={(next) => setDeals((ds) => ds.map((x) => (x.deal_id === d.deal_id ? { ...x, owner_user_ids: next } : x)))}
                        />
                      </td>
                      <td className="py-2 px-2">
                        <DealTagChipEditor
                          token={token}
                          dealId={d.deal_id}
                          allTags={tags}
                          value={d.tag_ids}
                          onChange={(next) => dealTagChanged(d.deal_id, next)}
                          onTagsChanged={refreshTags}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {deals.length > 200 && (
                <p className="text-[#ebebeb]/40 font-mono text-[10px] text-center pt-2">
                  Showing first 200 of {deals.length} — narrow the search to see more.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DealTagsManager;
